import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import EventSystem from './EventSystem';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import RmToolTip from './rmToolTip';
import './css/chatModule.css';
import '../node_modules/react-bootstrap-table/dist/react-bootstrap-table-all.min.css';
import Slider from 'react-rangeslider';
import './css/rangeslider.css';
import moment from 'moment';
import { CSVDownload } from 'react-csv';
import GlobalFunction from './globalFunction';
import { Tile, Title, Body, LoggingIndicator } from './style/tile.style';

// Cross-browser Page Visibility API
let hidden, visibilityChange;
if (typeof document.hidden !== 'undefined') {
    hidden = 'hidden'; visibilityChange = 'visibilitychange';
} else if (typeof document.msHidden !== 'undefined') {
    hidden = 'msHidden'; visibilityChange = 'msvisibilitychange';
} else if (typeof document.webkitHidden !== 'undefined') {
    hidden = 'webkitHidden'; visibilityChange = 'webkitvisibilitychange';
}

export default function ChatDisplayModule({ parameters, onSendMessage, enableModuleLogging, addLogEntry }) {
    const containerRef = useRef(null);
    const fontSizeCheckerRef = useRef(null);
    const chatListRef = useRef(null);
    const timerIDRef = useRef(0);

    const storageKey = parameters.config.module_name + '_' + parameters.config.event_screen_module_id;
    const live = useRef({
        volume: JSON.parse(localStorage.getItem(storageKey)) ?? 100,
        tabHasFocus: true,
        documentTitle: document.title,
        waitingForChatHistory: true,
        messageCount: 0,
    });

    const [chatHistory, setChatHistory] = useState([{ count: 9999889999 }]);
    const [volume, setVolume] = useState(live.current.volume);
    const [volumeControlVisible, setVolumeControlVisible] = useState(false);
    const [audioPlayable, setAudioPlayable] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [downloadStub, setDownloadStub] = useState([]);
    const [deleteAdditionalMessage, setDeleteAdditionalMessage] = useState(null);
    const [headingFontSize, setHeadingFontSize] = useState(0);
    const [loggingModule, setLoggingModule] = useState(false);
    const [highlight, setHighlight] = useState(false);

    function updateDimensions() {
        if (!containerRef.current) return;
        let fs = GlobalFunction.getFontSize(parameters.config.description, containerRef.current.clientWidth - 80, 30, fontSizeCheckerRef.current);
        if (fs > 22) fs = 22;
        setHeadingFontSize(fs);
    }

    useEffect(() => {
        enableModuleLogging();
        window.addEventListener('resize', updateDimensions);
        updateDimensions();

        const moduleId = parameters.config.event_screen_module_id;
        EventSystem.subscribe('ChatControlModule', showMessage);
        EventSystem.subscribe('ChatDisplayModule', showMessage);
        EventSystem.subscribe('logStatus' + moduleId, setLoggingModule);
        EventSystem.subscribe('highlightModule' + moduleId, setHighlight);
        document.addEventListener(visibilityChange, checkFocus, true);

        onSendMessage({
            action: 'getChatHistory',
            sourceModuleName: parameters.config.module_name,
            event_grouping_seq: parameters.config.event_grouping_seq,
            customerID: parameters.userProfile.customer_id,
            siteID: parameters.userProfile.site_id,
            eventID: parameters.config.event_id,
        });

        return () => {
            window.removeEventListener('resize', updateDimensions);
            EventSystem.unsubscribe('ChatControlModule', showMessage);
            EventSystem.unsubscribe('ChatDisplayModule', showMessage);
            EventSystem.unsubscribe('logStatus' + moduleId, setLoggingModule);
            EventSystem.unsubscribe('highlightModule' + moduleId, setHighlight);
            document.removeEventListener(visibilityChange, checkFocus, true);
        };
    }, []);

    // Auto-scroll to bottom when chat history updates
    useEffect(() => {
        if (chatListRef.current) {
            chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
        }
    }, [chatHistory]);

    function checkFocus() {
        const hasFocus = !document[hidden];
        live.current.tabHasFocus = hasFocus;
        if (hasFocus) {
            document.title = live.current.documentTitle;
            live.current.messageCount = 0;
        }
    }

    function showMessage(data) {
        if (parameters.config.event_grouping_seq !== data.event_grouping_seq) return;
        if (data.action === 'chat') {
            const { chatParams } = data;
            setChatHistory(prev => {
                const newRow = {
                    name: chatParams.name,
                    message: chatParams.message,
                    utcTime: chatParams.utcTime,
                    count: prev.length,
                    messageDateTime: moment(chatParams.utcTime).format('ll LT'),
                };
                return [...prev, newRow];
            });
            addLogEntry('Recv ' + chatParams.senderSequence + ': ' + chatParams.message);

            const beep = new Audio('chatBeep.mp3');
            beep.volume = live.current.volume / 100;
            const promise = beep.play();
            if (promise !== undefined) {
                promise.then(() => setAudioPlayable(true)).catch(() => setAudioPlayable(false));
            }

            if (!live.current.tabHasFocus) {
                live.current.messageCount += 1;
                document.title = '(' + live.current.messageCount + ') ' + live.current.documentTitle;
            }
        } else if (data.action === 'getChatHistory' && live.current.waitingForChatHistory) {
            let count = 0;
            const history = data.chatParams.map((row) => ({
                ...row.chatParams,
                messageDateTime: moment(row.chatParams.utcTime).format('ll LT'),
                count: count++,
            }));
            live.current.waitingForChatHistory = false;
            setChatHistory(history);
        } else if (data.action === 'clearChatHistory') {
            setChatHistory([{ count: 9999889999 }]);
        }
    }

    function handleVolumeChange(value) {
        live.current.volume = value;
        setVolume(value);
        localStorage.setItem(storageKey, JSON.stringify(value));
        clearTimeout(timerIDRef.current);
        timerIDRef.current = setTimeout(() => {
            if (live.current.volume === value) setVolumeControlVisible(false);
        }, 2000);
    }

    function downloadCSVFile() {
        const headers = [{ label: 'Date', key: 'messageDateTime' }, { label: 'Name', key: 'name' }, { label: 'Message', key: 'message' }];
        setDownloadStub([<CSVDownload key='download' data={chatHistory} headers={headers} target="_blank" />]);
        setDeleteAdditionalMessage(null);
    }

    function deleteChatHistory() {
        if (downloadStub.length === 0) {
            setDeleteAdditionalMessage(
                <div>
                    <span>Deleting the chat history will remove it from this and all other devices and cannot be undone.
                        As a safety precaution we suggest that you download a copy of the chat history first.</span>
                    <br /><br />
                    <span>If you are certain that you wish to delete the chat history, click the Confirm Delete button.</span>
                    <br /><br />
                    <Button className="btn btn-md btn-danger" style={{ textShadow: 'none' }} onClick={requestToClearChatHistory}>Confirm Delete</Button>
                </div>
            );
        } else {
            requestToClearChatHistory();
        }
    }

    function requestToClearChatHistory() {
        onSendMessage({
            action: 'clearChatHistory',
            sourceModuleName: parameters.config.module_name,
            event_grouping_seq: parameters.config.event_grouping_seq,
            customerID: parameters.userProfile.customer_id,
            siteID: parameters.userProfile.site_id,
            eventID: parameters.config.event_id,
        });
    }

    function closeModal() {
        setShowModal(false);
        setDownloadStub([]);
    }

    const chatHeading = parameters.config.description.length > 0 ? parameters.config.description : 'Chat';
    const bodyHeight = parameters.containerHeight.container - 2 - 37 - 2;
    const optionStyle = { borderBottom: '1px solid ' + GlobalFunction.getPaletteColour(parameters.config.colour) + '66' };

    let volumeIconName = volume === 0 ? 'volume_mute' : volume > 50 ? 'volume_up' : 'volume_down';
    const soundStyle = { position: 'absolute', right: '37px', top: '0px', fontSize: '22px', height: '30px', width: '35px', display: 'inline-block', paddingLeft: '3px', paddingTop: '7px' };

    return (
        <Tile ref={containerRef} highlight={highlight} height={parameters.containerHeight.container - 2 + 'px'}>
            <Modal show={showModal} autoFocus>
                <Modal.Header>
                    <Modal.Title>Download and Clear Chat History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <table><tbody>
                        <tr>
                            <td>
                                <Button style={{ margin: '5px', textShadow: 'none', width: '180px' }}
                                    className="btn btn-md btn-success"
                                    onClick={downloadCSVFile}>
                                    Download Chat History
                                </Button>
                            </td>
                            <td>{downloadStub}</td>
                        </tr>
                        <tr>
                            <td style={{ verticalAlign: 'top' }}>
                                <Button style={{ margin: '5px', textShadow: 'none', width: '180px' }}
                                    className="btn btn-md btn-success"
                                    onClick={deleteChatHistory}>
                                    Delete Chat History
                                </Button>
                            </td>
                            <td style={{ paddingLeft: '10px' }}>{deleteAdditionalMessage}</td>
                        </tr>
                    </tbody></table>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={closeModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Title fontSize={headingFontSize + 'px'}>
                <span style={{ paddingLeft: '5px' }}>{chatHeading}</span>
                <RmToolTip itemText="history" itemType="icon" placement="bottom"
                    onClick={() => setShowModal(true)}
                    style={{ position: 'absolute', right: 0, top: 0, paddingLeft: '3px', paddingTop: '7px', height: '30px', width: '35px', fontSize: '22px' }}
                    toolTipText={<span>Save and clear<br />chat history</span>} />
                {audioPlayable ? (
                    <RmToolTip itemText={volumeIconName} itemType="icon" placement="bottom"
                        key={'volumeInd' + parameters.config.event_grouping_seq}
                        onClick={() => setVolumeControlVisible(v => !v)}
                        style={soundStyle}
                        toolTipText={<span>Change Volume</span>} />
                ) : (
                    <RmToolTip itemText="warning" itemType="icon" placement="bottom"
                        key={'volumeInd' + parameters.config.event_grouping_seq}
                        noClick={true}
                        style={soundStyle}
                        toolTipText={<span>You must give your<br />browser permission<br />to play audio</span>} />
                )}
            </Title>

            <Body height={bodyHeight + 'px'}>
                <ul name="chatHistory"
                    ref={chatListRef}
                    style={{
                        marginLeft: '10px', paddingLeft: 0, paddingRight: '10px',
                        width: 'calc(100% - 10px)', borderStyle: 'none', overflowX: 'auto',
                        listStyle: 'none', height: '100%',
                        backgroundColor: parameters.colours.backgroundColor,
                        color: parameters.colours.color,
                    }}>
                    {chatHistory.map((row, y) => (
                        <li key={'Message' + y} style={optionStyle}>
                            <div style={{ width: '100%', paddingTop: '2px', paddingBottom: '2px' }}>
                                <div style={{ width: '20%', display: 'inline-block', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                    <RmToolTip itemText={row.name} itemType="text" placement="bottom"
                                        ellipsis noClick={true}
                                        toolTipText={row.name + ' ' + moment(row.utcTime).format('LT')} />
                                </div>
                                <div style={{ width: '2%', display: 'inline-block' }}>{' '}</div>
                                <div style={{ width: '78%', display: 'inline-block' }}>{row.message}</div>
                            </div>
                        </li>
                    ))}
                </ul>
            </Body>

            {volumeControlVisible && (
                <div style={{ position: 'absolute', right: '75px', top: '11px' }}>
                    <Slider
                        tooltip={false}
                        value={volume}
                        handleLabel={volume.toString(10)}
                        onChange={handleVolumeChange} />
                </div>
            )}

            <div style={{ position: 'fixed', top: '-2000px', left: '-2000px', backgroundColor: 'pink', whiteSpace: 'pre' }}
                ref={fontSizeCheckerRef} />
            <LoggingIndicator logStatus={loggingModule} />
        </Tile>
    );
}
